import type {Dominio} from "services-comun-tiempo/modules/portal";
import type {Prioridad} from "services-comun/modules/browser/scheduler";
import {PromiseDelayed} from "services-comun/modules/browser/promise";
import {info} from "services-comun/modules/browser/log";

import type {Controlador} from "../controlador";
import type {IConf} from "../../utiles/config";
import {ModuloError} from "./error";

export interface IModuloBase {
    global: IConf;
    document: Document;
    dominio: Dominio;
    window: Window;
}

export interface IModuloBuilder {
    build(cfg: IModuloBase): Promise<Modulo[]>;
}

export interface IModulo extends IModuloBase {
    id: string;
    selector: string;
    prioridad?: Prioridad;
}

export abstract class Modulo<T extends IModulo = IModulo> {
    /* STATIC */

    /* INSTANCE */
    public uid: Symbol;
    protected elements: Element[];
    protected controllers: Map<Element, Controlador|undefined>;
    private error?: Error;
    private executed: boolean;

    public get id(): string { return this.cfg.id; }
    public get enabled(): boolean { return this.elements.length > 0; }
    public get disabled(): boolean { return this.elements.length == 0; }
    public get length(): number { return this.elements.length; }
    public readonly nombre: string;

    protected constructor(protected cfg: T) {
        this.uid = Symbol(this.cfg.id);
        this.elements = [];
        // this.elements = Modulo.NODE_DEFAULT;
        this.controllers = new Map<Element, Controlador|undefined>();
        this.executed = false;

        this.nombre = `${this.cfg.id}[${this.cfg.selector}]`;
    }

    public async init(): Promise<void> {
        if (this.error) {
            return Promise.reject(this.error);
        }
        if (this.executed) {
            return;
        }

        await PromiseDelayed(0, this.cfg.prioridad);

        this.executed = true;
        this.elements = Array.from(document.querySelectorAll(this.cfg.selector));

        info(this.nombre, "Iniciando");

        if (this.disabled) {
            this.error = ModuloError.info(`No hay elementos`);
            return Promise.reject(this.error);
        }

        try {
            await this.run();
        } catch (err) {
            if (err instanceof Error) {
                this.error = err;
            } else {
                this.error = ModuloError.error(`Error ejecutando el módulo ${JSON.stringify(err)}`);
            }

            return Promise.reject(this.error);
        }

    }

    private async run(): Promise<void> {
        if (this.disabled) {
            return;
        }

        await Promise.all(Array.from(this.elements).map(element => this.runElement(element)));
    }

    protected abstract controller(element: Element): Promise<Controlador>;
    protected abstract runElement(element: Element): Promise<void>;
}
